import React, { useEffect } from "react";
import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { NarratorProvider, unlockNarrator } from "@/lib/narrator";
import { unlockAudioLogo } from "@/lib/audioLogo";
import Landing from "@/pages/Landing";
import SoulIntake from "@/pages/SoulIntake";
import WelcomeCeremony from "@/pages/WelcomeCeremony";
import CharacterReveal from "@/pages/CharacterReveal";
import DeckReveal from "@/pages/DeckReveal";
import AppShell from "@/pages/AppShell";
import AuthCallback from "@/pages/AuthCallback";
import Splash from "@/components/Splash";
import NarratorControls from "@/components/NarratorControls";
import HeaderLogout from "@/components/HeaderLogout";
import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#080808]">
        <div className="font-overline breathe">Attuning…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // OAuth callback: emergent returns to <redirect_url>#session_id=...
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const hideNarrator = location.pathname === "/";
  return (
    <>
      {!hideNarrator && <NarratorControls />}
      {!hideNarrator && <HeaderLogout />}
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/intake" element={<Protected><SoulIntake /></Protected>} />
        <Route path="/ceremony/welcome" element={<Protected><WelcomeCeremony /></Protected>} />
        <Route path="/ceremony/reveal" element={<Protected><CharacterReveal /></Protected>} />
        <Route path="/ceremony/deck" element={<Protected><DeckReveal /></Protected>} />
        <Route path="/app/*" element={<Protected><AppShell /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  useEffect(() => {
    const unlock = () => {
      unlockAudioLogo();
      unlockNarrator();
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);
  return (
    <div className="App">
      <Splash />
      <BrowserRouter>
        <AuthProvider>
          <NarratorProvider>
            <Toaster
              position="top-center"
              toastOptions={{
                style: {
                  background: "#111111",
                  color: "#F4EFE6",
                  border: "1px solid rgba(200,169,110,0.25)",
                  fontFamily: "DM Sans",
                },
              }}
            />
            <AppRoutes />
          </NarratorProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
